import React, { useEffect, useRef, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import axios from 'axios';

import config from '../../config';
import { ensureCurrentUser } from '../../util/data';
import { fetchCurrentUser } from '../../ducks/user.duck';
import MainDashboardWrapper from '../../components/MainDashboardWrapper/MainDashboardWrapper';

// ── Brand palette ───────────────────────────────────────────────────────────
const COLOR_DEEP_PURPLE = '#1E1040';
const COLOR_PURPLE = '#8B5CF6';
const COLOR_PURPLE_LIGHT = '#C084FC';
const COLOR_GOLD = '#FBBF24';
const COLOR_GOLD_DEEP = '#F59E0B';

// Plan → tier classification. Plan IDs come in via ?plan= on the redirect URL
// from Chargebee. Lifetime gets the loudest celebration. Verified gets a
// purple+gold mix. Network gets a calmer purple confetti.
function planToTier(plan) {
  if (!plan) return 'network';
  if (plan === 'Legacy-Subscriber---Lifetime') return 'lifetime';
  const lower = plan.toLowerCase();
  if (lower.indexOf('verified') !== -1) return 'verified';
  if (lower.indexOf('network') !== -1) return 'network';
  return 'network';
}

// Friendly display name for the subscription confirmation chip.
function planToFriendlyName(plan) {
  switch (plan) {
    case 'Network-Member---Monthly': return 'Network Member — Monthly';
    case 'Network-Member-Annual': return 'Network Member — Annual';
    case 'Verified-Coach-Monthly': return 'Verified Coach — Monthly';
    case 'Verified-Coach---Annual': return 'Verified Coach — Annual';
    case 'First-Mover-Network-Monthly': return 'First Mover Network — Monthly';
    case 'First-Mover-Network-Annual': return 'First Mover Network — Annual';
    case 'First-Mover-Verified-Monthly': return 'First Mover Verified — Monthly';
    case 'First-Mover-Verified-Annual': return 'First Mover Verified — Annual';
    case 'Legacy-Subscriber---Lifetime': return 'Viveka First 100 — Lifetime';
    default: return plan || 'Viveka Subscriber';
  }
}

// ── Confetti hook ───────────────────────────────────────────────────────────
// Inline canvas particle system — no external dependency. Intensity and
// palette scale with tier. Lifetime gets staggered bursts at 2s and 4.5s
// to feel like a true celebration; Verified gets one extra burst at 2s.
function useConfetti(tier) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof window === 'undefined') return undefined;
    const ctx = canvas.getContext('2d');

    const sizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    sizeCanvas();

    const palettes = {
      network: ['#8B5CF6', '#C084FC', '#A78BFA', '#E9D5FF'],
      verified: ['#8B5CF6', '#C084FC', '#FBBF24', '#A78BFA', '#E9D5FF', '#FCD34D'],
      lifetime: ['#FBBF24', '#F59E0B', '#FCD34D', '#FEF3C7', '#8B5CF6', '#C084FC'],
    };
    const counts = { network: 140, verified: 240, lifetime: 380 };
    const palette = palettes[tier] || palettes.network;
    const initialCount = counts[tier] || 140;

    const particles = [];

    const spawn = (count, fromTop) => {
      for (let i = 0; i < count; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: fromTop ? -20 : -20 - Math.random() * canvas.height * 0.5,
          vx: (Math.random() - 0.5) * 5,
          vy: 2 + Math.random() * 5,
          size: 4 + Math.random() * 8,
          rotation: Math.random() * Math.PI * 2,
          rotationSpeed: (Math.random() - 0.5) * 0.22,
          color: palette[Math.floor(Math.random() * palette.length)],
          shape: Math.random() > 0.6 ? 'circle' : 'square',
          life: 0,
          maxLife: 220 + Math.random() * 220,
        });
      }
    };

    spawn(initialCount, false);

    const burstTimers = [];
    if (tier === 'lifetime') {
      burstTimers.push(setTimeout(() => spawn(200, true), 2000));
      burstTimers.push(setTimeout(() => spawn(160, true), 4500));
    } else if (tier === 'verified') {
      burstTimers.push(setTimeout(() => spawn(120, true), 2000));
    }

    let rafId;
    const tick = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.05;
        p.rotation += p.rotationSpeed;
        p.life += 1;
        if (p.y > canvas.height + 60 || p.life > p.maxLife) {
          particles.splice(i, 1);
          continue;
        }
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = Math.max(0, 1 - p.life / p.maxLife);
        if (p.shape === 'circle') {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
        }
        ctx.restore();
      }
      if (particles.length > 0) {
        rafId = window.requestAnimationFrame(tick);
      }
    };
    rafId = window.requestAnimationFrame(tick);

    window.addEventListener('resize', sizeCanvas);

    return () => {
      window.cancelAnimationFrame(rafId);
      burstTimers.forEach(t => clearTimeout(t));
      window.removeEventListener('resize', sizeCanvas);
    };
  }, [tier]);

  return canvasRef;
}

// ── Component ───────────────────────────────────────────────────────────────
const WelcomePage = props => {
  const { currentUser, onFetchCurrentUser, history, location } = props;

  const queryParams = new URLSearchParams((location && location.search) || '');
  const plan = queryParams.get('plan') || '';
  const tier = planToTier(plan);
  const planName = planToFriendlyName(plan);

  const ensuredUser = ensureCurrentUser(currentUser);
  const firstName =
    ensuredUser.attributes &&
    ensuredUser.attributes.profile &&
    ensuredUser.attributes.profile.firstName;

  // Tracks whether the account's cached subscriptionDetails already reflect
  // the plan we just paid for — used below to stop polling early once the
  // Chargebee webhook → Sharetribe metadata sync has actually landed.
  const syncedPlanId =
    ensuredUser.attributes &&
    ensuredUser.attributes.profile &&
    ensuredUser.attributes.profile.metadata &&
    ensuredUser.attributes.profile.metadata.subscriptionDetails &&
    ensuredUser.attributes.profile.metadata.subscriptionDetails.planId;
  const syncedPlanIdRef = useRef(syncedPlanId);
  useEffect(() => {
    syncedPlanIdRef.current = syncedPlanId;
  }, [syncedPlanId]);

  // Fetch the seat number for Lifetime so we can render "#X of 100".
  // /eligible-plans returns `lifetime.seatsSold` — after the coach's own
  // subscription is created, that count reflects their seat.
  const [seatNumber, setSeatNumber] = useState(null);
  useEffect(() => {
    if (tier !== 'lifetime') return undefined;
    let cancelled = false;
    const fetchSeat = async () => {
      try {
        const createdAt =
          ensuredUser.attributes && ensuredUser.attributes.createdAt;
        const res = await axios.post(
          `${config.subscriptionServiceUrl}eligible-plans`,
          { createdAt },
          { headers: { 'content-type': 'application/json' } }
        );
        if (!cancelled && res.data && res.data.lifetime &&
          typeof res.data.lifetime.seatsSold === 'number') {
          setSeatNumber(res.data.lifetime.seatsSold);
        }
      } catch (err) {
        // Non-fatal — just hide the seat reveal if the count isn't available.
        // eslint-disable-next-line no-console
        console.warn('[WelcomePage] could not fetch lifetime seat number', err);
      }
    };
    fetchSeat();
    return () => {
      cancelled = true;
    };
  }, [tier, ensuredUser.attributes && ensuredUser.attributes.createdAt]);

  // Refresh current user after landing so the new subscription tier pulled
  // from the Chargebee webhook → Sharetribe metadata sync is current. A
  // single fetch at a fixed delay used to leave the SubscriptionExpiryBanner
  // (rendered by MainDashboardWrapper on every dashboard page, this one
  // included) showing stale "expired"/pending data whenever the webhook
  // hadn't landed yet by that one attempt — only a manual reload picked up
  // the sync after that. Retry a few times instead, and stop as soon as
  // subscriptionDetails.planId actually matches the plan we just paid for.
  useEffect(() => {
    if (!plan || typeof onFetchCurrentUser !== 'function') return undefined;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 6; // ~1.5s, 3.5s, 5.5s, 7.5s, 9.5s, 11.5s after mount
    let timeoutId;

    const poll = () => {
      if (cancelled || syncedPlanIdRef.current === plan) return;
      attempts += 1;
      onFetchCurrentUser();
      if (attempts < maxAttempts) {
        timeoutId = setTimeout(poll, 2000);
      }
    };
    timeoutId = setTimeout(poll, 1500);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [plan, onFetchCurrentUser]);

  // Funnel analytics: checkout_completed (Chargebee confirmed payment
  // and redirected here) + welcome_banner_shown (the celebration UI
  // is rendered). Both fire once per page load.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.dataLayer) return;
    window.dataLayer.push({
      event: 'checkout_completed',
      plan,
      tier,
    });
    window.dataLayer.push({
      event: 'welcome_banner_shown',
      tier,
    });
    // Clear the signupIntent now that the funnel is complete — no need
    // to keep highlighting it on future visits to the pricing page.
    try {
      if (window.sessionStorage) {
        window.sessionStorage.removeItem('signupIntent');
        window.sessionStorage.removeItem('pendingPublishListingId');
      }
    } catch (e) { /* non-fatal */ }
  }, [plan, tier]);

  const canvasRef = useConfetti(tier);

  // Tier-specific copy
  let title;
  let subtitle;
  let tagline;
  let badgeText;
  let badgeBg;
  let badgeShadow;
  if (tier === 'lifetime') {
    title = firstName ? `Welcome to Viveka First 100, ${firstName}.` : 'Welcome to Viveka First 100.';
    subtitle = 'Your founder rate is locked. For life.';
    tagline = seatNumber
      ? `You are one of the first 100 coaches to claim Viveka as your own. Your listing carries the Lifetime badge — and never renews.`
      : 'One of 100 founders. Your listing carries the Lifetime badge — and never renews.';
    badgeText = 'VIVEKA FIRST 100 · LIFETIME';
    badgeBg = `linear-gradient(135deg, ${COLOR_GOLD} 0%, ${COLOR_GOLD_DEEP} 100%)`;
    badgeShadow = '0 10px 28px rgba(251,191,36,0.4)';
  } else if (tier === 'verified') {
    title = firstName ? `Welcome, ${firstName}.` : 'Welcome to Verified.';
    subtitle = 'Your Verified subscription is active.';
    tagline = 'Once your credential is reviewed and approved, your listing will display the Verified badge — and you become eligible for enterprise opportunities, priority placement, and spotlight features.';
    badgeText = 'VERIFIED COACH';
    badgeBg = `linear-gradient(135deg, ${COLOR_PURPLE} 0%, ${COLOR_DEEP_PURPLE} 100%)`;
    badgeShadow = '0 8px 20px rgba(139,92,246,0.35)';
  } else {
    title = firstName ? `Welcome, ${firstName}.` : 'Welcome to the Viveka network.';
    subtitle = 'Your Network membership is active.';
    tagline = 'You can now publish your listings and start accepting clients on the marketplace.';
    badgeText = 'NETWORK MEMBER';
    badgeBg = COLOR_PURPLE_LIGHT;
    badgeShadow = '0 4px 12px rgba(192,132,252,0.3)';
  }

  const go = path => e => {
    if (e && e.preventDefault) e.preventDefault();
    if (history && history.push) history.push(path);
  };

  return (
    <MainDashboardWrapper>
      <div style={{ position: 'relative', minHeight: '80vh', overflow: 'hidden' }}>
        {/* Confetti canvas: full-viewport, non-interactive overlay */}
        <canvas
          ref={canvasRef}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            pointerEvents: 'none',
            zIndex: 9999,
          }}
        />

        <div
          style={{
            maxWidth: '760px',
            margin: '0 auto',
            padding: '64px 24px 96px',
            textAlign: 'center',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            position: 'relative',
            zIndex: 1,
          }}
        >
          {/* Tier badge */}
          <div
            style={{
              display: 'inline-block',
              padding: '10px 20px',
              background: badgeBg,
              color: '#fff',
              fontWeight: 800,
              fontSize: '11px',
              letterSpacing: '0.2em',
              borderRadius: '999px',
              boxShadow: badgeShadow,
              marginBottom: '36px',
            }}
          >
            {badgeText}
          </div>

          {/* Headline */}
          <h1
            style={{
              fontWeight: 900,
              fontSize: tier === 'lifetime' ? '48px' : '40px',
              lineHeight: 1.1,
              color: COLOR_DEEP_PURPLE,
              letterSpacing: '-0.025em',
              marginBottom: '18px',
            }}
          >
            {title}
          </h1>

          {/* Subtitle */}
          <p
            style={{
              fontSize: '21px',
              color: '#525252',
              fontWeight: 500,
              marginBottom: '32px',
              lineHeight: 1.4,
            }}
          >
            {subtitle}
          </p>

          {/* Lifetime seat reveal — only shown when the count is known */}
          {tier === 'lifetime' && seatNumber !== null && (
            <div
              style={{
                display: 'inline-block',
                padding: '28px 56px',
                background: '#fff',
                border: `2px solid ${COLOR_GOLD}`,
                borderRadius: '24px',
                boxShadow: '0 14px 40px rgba(251,191,36,0.22)',
                marginBottom: '32px',
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  fontSize: '11px',
                  letterSpacing: '0.2em',
                  color: COLOR_GOLD_DEEP,
                  marginBottom: '10px',
                }}
              >
                YOUR FOUNDER SEAT
              </div>
              <div
                style={{
                  fontWeight: 900,
                  fontSize: '72px',
                  lineHeight: 1,
                  color: COLOR_DEEP_PURPLE,
                  letterSpacing: '-0.02em',
                }}
              >
                #{seatNumber}
                <span style={{ fontSize: '26px', color: '#999', fontWeight: 600 }}>
                  {' '}/ 100
                </span>
              </div>
            </div>
          )}

          {/* Tagline */}
          <p
            style={{
              fontSize: '16px',
              color: '#666',
              lineHeight: 1.65,
              marginBottom: '40px',
              maxWidth: '560px',
              margin: '0 auto 40px',
            }}
          >
            {tagline}
          </p>

          {/* Subscription confirmation chip */}
          <div
            style={{
              padding: '14px 22px',
              background: 'rgba(139,92,246,0.06)',
              border: '1px solid rgba(139,92,246,0.18)',
              borderRadius: '12px',
              color: COLOR_DEEP_PURPLE,
              fontSize: '14px',
              fontWeight: 600,
              marginBottom: '44px',
              display: 'inline-block',
            }}
          >
            <span style={{ opacity: 0.7, fontWeight: 500 }}>Subscription: </span>
            {planName}
          </div>

          {/* CTAs */}
          <div
            style={{
              display: 'flex',
              gap: '14px',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <a
              href="/v-coach/dashboard"
              onClick={go('/v-coach/dashboard')}
              style={{
                display: 'inline-block',
                padding: '14px 34px',
                background: COLOR_DEEP_PURPLE,
                color: '#fff',
                fontWeight: 700,
                fontSize: '15px',
                borderRadius: '999px',
                textDecoration: 'none',
                letterSpacing: '0.01em',
                boxShadow: '0 8px 20px rgba(30,16,64,0.18)',
              }}
            >
              Continue to dashboard →
            </a>
            <a
              href="/account/payments/pricing"
              onClick={go('/account/payments/pricing')}
              style={{
                display: 'inline-block',
                padding: '14px 30px',
                background: 'transparent',
                color: COLOR_DEEP_PURPLE,
                fontWeight: 600,
                fontSize: '15px',
                borderRadius: '999px',
                border: '1.5px solid rgba(30,16,64,0.18)',
                textDecoration: 'none',
              }}
            >
              Manage subscription
            </a>
          </div>
        </div>
      </div>
    </MainDashboardWrapper>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  return { currentUser };
};

const mapDispatchToProps = dispatch => ({
  onFetchCurrentUser: () => dispatch(fetchCurrentUser()),
});

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(WelcomePage);
